/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { PublishDialog } from './PublishDialog';
import type { PublicationsCenterProps, PublicationRow } from './publications-center/types';
import { mergePropertyPublicationRows } from './publications-center/merge-property-rows';
import { ConfirmDialog } from './publications-center/ConfirmDialog';
import { usePublications, resolveRetryImages } from './publications-center/use-publications';
import { PublicationRowItem } from './publications-center/PublicationRowItem';
import { ChannelGroupTitle, ChannelSummaryStrip, NotPublishedSection } from './publications-center/ChannelSections';
import { groupRowsByChannel, summarizeChannels, unpublishedPortals } from './publications-center/channel-groups';
import { useAdDialogs } from './ad-dialogs/use-ad-dialogs';
import { usePendingPublicationsRefresh } from './publications-center/use-pending-publications-refresh';
import { leadAdErrorCode, leadAdErrorDetail } from '@/shared/utils/ad-dialog-helpers';
import { adSettingsHref } from './ad-dialogs/AdSetupNotice';
import { usePropertyImages } from './PropertyDetailView/hooks/usePropertyImages';
import { Button, Text } from '@/shared/ui';

interface ExtendedProps {
  inline?: boolean;
  autoEditPublicationId?: string;
  onEditSaveSuccess?: () => void;
  onEditCancel?: () => void;
  /** Opens the property's edit form (OLX "Editar": the ad is the property). */
  onEditProperty?: () => void;
}

/** Imperative handle so a page-level "refresh" control (e.g. the publications
 * tab's header button) can re-fetch this panel's data without owning it. */
export interface PublicationsCenterHandle {
  refresh: () => Promise<void>;
}

/**
 * Panel that lists a property's portal publications with inline edit, remove,
 * and "new publication" actions. Renders either inline or as a floating card,
 * and can auto-open a specific publication for editing.
 */
export const PublicationsCenter = forwardRef<PublicationsCenterHandle, PublicationsCenterProps & ExtendedProps>(
  function PublicationsCenter({
    propertyId,
    agencyId,
    onClose,
    inline = false,
    propertyImages = [],
    autoEditPublicationId,
    onEditSaveSuccess,
    onEditCancel,
    onEditProperty,
  }, ref) {
    // property.media.images can be empty while the property still has real
    // images living in S3 (crawled/legacy listings) -- the same gap
    // PublishDialog already works around via this same hook. Retrying an
    // Instagram publish must not fail the images gate on that gap alone.
    const { imageUrls } = usePropertyImages(agencyId, propertyId, propertyImages);
    const adDialogs = useAdDialogs();
    const pub = usePublications(propertyId, agencyId, propertyImages, {
      onSaveSuccess: onEditSaveSuccess,
      retryImages: resolveRetryImages(propertyImages, imageUrls),
      confirmSpend: adDialogs.confirmSpend,
      showAdError: adDialogs.showAdError,
    });
    const { t } = pub;
    usePendingPublicationsRefresh(pub.publications, pub.fetchData);
    const [confirmRow, setConfirmRow] = useState<PublicationRow | null>(null);
    const [showNewPublication, setShowNewPublication] = useState(false);
    const autoEditFiredRef = useRef(false);

    useImperativeHandle(ref, () => ({ refresh: pub.fetchData }), [pub.fetchData]);

    const effectiveCancelEdit = onEditCancel ?? pub.cancelEdit;

    function getPortalLabel(portal: string) {
      const key = `publications.portal.${portal}` as const;
      const label = t(key as any);
      return label !== key ? label : portal.toUpperCase();
    }

    function getStatusLabel(status: string) {
      const key = `publications.status.${status}` as const;
      const label = t(key as any);
      return label !== key ? label : status;
    }

    function showLeadAdError(row: PublicationRow) {
      adDialogs.showAdError({
        kind: 'leadAd',
        detail: leadAdErrorDetail(row.lead_ad_error),
        code: leadAdErrorCode(row.lead_ad_error),
      });
    }

    const rows = mergePropertyPublicationRows(
      pub.publications,
      pub.catalogEntries,
      propertyId,
      getPortalLabel,
    );

    useEffect(() => {
      if (!autoEditPublicationId || pub.loading || autoEditFiredRef.current || rows.length === 0) return;
      const target = rows.find(
        (r) => r.post_id === autoEditPublicationId || r.listing_id === autoEditPublicationId,
      );
      if (target) {
        autoEditFiredRef.current = true;
        pub.handleEdit(target);
      }
    }, [autoEditPublicationId, pub.loading, rows.length]);

    const hasPublications = rows.length > 0;
    const groups = groupRowsByChannel(rows);
    const notPublished = unpublishedPortals(rows, pub.publishablePortals);

    if (showNewPublication) {
      return (
        <PublishDialog
          propertyId={propertyId}
          agencyId={agencyId}
          propertyImages={propertyImages}
          onClose={() => { setShowNewPublication(false); pub.fetchData(); }}
        />
      );
    }

    const renderRow = (row: PublicationRow) => (
      <PublicationRowItem
        key={row.key}
        row={row}
        t={t}
        connectedPortals={pub.connectedPortals}
        removing={pub.removing}
        updating={pub.updating}
        editingRow={pub.editingRow}
        editText={pub.editText}
        setEditText={pub.setEditText}
        loadingEditContent={pub.loadingEditContent}
        editImages={pub.editImages}
        editImagesOriginal={pub.editImagesOriginal}
        setEditImages={pub.setEditImages}
        getStatusLabel={getStatusLabel}
        onEdit={pub.handleEdit}
        onEditProperty={onEditProperty}
        onConfirmRemove={setConfirmRow}
        onEditSave={pub.handleEditSave}
        onCancelEdit={effectiveCancelEdit}
        boostingRow={pub.boostingRow}
        onBoost={pub.handleBoost}
        onRetry={pub.handleRetry}
        retrying={pub.retrying}
        onShowAdError={showLeadAdError}
        fbHasPaymentMethod={pub.fbHasPaymentMethod}
        tokenExpiresAt={row.portal === 'instagram' ? pub.igTokenExpiresAt : pub.fbTokenExpiresAt}
        adAccountId={pub.fbAdAccountId}
        defaultBoostBudget={row.portal === 'instagram' ? pub.igLeadAdBudget : pub.fbLeadAdBudget}
        defaultBoostDuration={row.portal === 'instagram' ? pub.igLeadAdDuration : pub.fbLeadAdDuration}
        boostMissing={row.portal === 'instagram' ? pub.adReadiness.instagram.boost : pub.adReadiness.facebook.boost}
        adSettingsHref={adSettingsHref(agencyId)}
      />
    );

    const content = (
      <>
        {!inline && (
          <div className="flex items-center justify-between">
            <Text as="span" className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('publications.title')}</Text>
            <Button type="button" variant="none" onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer bg-transparent border-none" data-testid="close-btn">
              <Cross2Icon width={12} height={12} />
            </Button>
          </div>
        )}

        {pub.loading ? (
          <div className="flex items-center justify-center min-h-[80px]">
            <Text className="m-0 text-xs text-[var(--text-tertiary)]">{t('publications.loading')}</Text>
          </div>
        ) : !hasPublications && notPublished.length === 0 ? (
          <div className="flex items-center justify-center min-h-[80px]">
            <Text className="m-0 text-xs text-[var(--text-tertiary)]">{t('publications.noPublications')}</Text>
          </div>
        ) : (
          <div className="space-y-(--space-4)">
            {hasPublications && <ChannelSummaryStrip summary={summarizeChannels(rows, pub.publishablePortals)} t={t} />}
            {groups.map((group) => (
              <div key={group.key} className="space-y-(--space-2)" data-testid={`channel-group-${group.key}`}>
                <ChannelGroupTitle title={t(`publications.groups.${group.key}`)} testId={`channel-group-title-${group.key}`} />
                {group.rows.map((row) => renderRow(row))}
              </div>
            ))}
            <NotPublishedSection
              portals={notPublished}
              getPortalLabel={getPortalLabel}
              onPublish={() => setShowNewPublication(true)}
              t={t}
            />
          </div>
        )}

        {!inline && (
          <Button type="button" onClick={() => setShowNewPublication(true)} className="w-full py-(--space-2) text-xs font-bold rounded-(--radius-md) cursor-pointer hover:opacity-90 transition-opacity" style={{ background: 'var(--seed-base)', color: 'var(--text-inverse)' }} data-testid="new-publication-btn">
            {t('publications.newPublication')}
          </Button>
        )}

        {adDialogs.dialogs}

        {confirmRow && (
          <ConfirmDialog
            message={t('publications.actions.confirmRemoveMessage', { portal: confirmRow.label })}
            onConfirm={() => { pub.handleRemove(confirmRow); setConfirmRow(null); }}
            onCancel={() => setConfirmRow(null)}
            confirmLabel={t('publications.actions.confirm')}
            cancelLabel={t('publications.actions.cancel')}
          />
        )}
      </>
    );

    if (inline) {
      return <div className="space-y-(--space-3)" data-testid="publications-center">{content}</div>;
    }

    return (
      <div className="p-(--space-4) rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-xl space-y-(--space-3)" data-testid="publications-center">
        {content}
      </div>
    );
  },
);
